import { useState, useMemo, useEffect, useRef } from 'react';
import { SPORT_TO_LEAGUE_ID } from '@low6-pickem/sports-config';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetFeedFixturesQuery } from '@/api/feed/getFeedFixtures';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import { Loader2, Calendar } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';

interface FixtureSelectorInlineProps {
  sport: string;
  selectedFixtureId?: string;
  onSelect: (fixtureId: string, fixture: any) => void;
  disabled?: boolean;
  errors?: any;
  touched?: any;
  /**
   * ISO datetime of the already-selected fixture. When present, the date
   * filter is seeded from this so the previously chosen fixture remains
   * visible in the dropdown on edit. Pass undefined for new contests.
   */
  initialScheduledDate?: string;
}

export function FixtureSelectorInline({
  sport,
  selectedFixtureId,
  onSelect,
  disabled = false,
  errors,
  touched,
  initialScheduledDate,
}: FixtureSelectorInlineProps) {
  const [selectedDate, setSelectedDate] = useState(() => {
    return formatInTimeZone(new Date(), 'America/New_York', 'yyyy-MM-dd');
  });

  // Seed the date filter from the saved scheduled date exactly once, the
  // first time we observe a non-empty value. This handles the async case
  // where Formik values are populated after mount via enableReinitialize
  // or a parent data fetch. Once seeded (or the user manually changes the
  // date) we never re-seed, so user edits aren't clobbered by later
  // Formik reinitialisation.
  const hasSeededFromInitialRef = useRef(false);
  useEffect(() => {
    if (hasSeededFromInitialRef.current) return;
    if (!initialScheduledDate) return;
    try {
      const seeded = formatInTimeZone(
        new Date(initialScheduledDate),
        'America/New_York',
        'yyyy-MM-dd'
      );
      setSelectedDate(seeded);
      hasSeededFromInitialRef.current = true;
    } catch {
      /* invalid date — leave default */
    }
  }, [initialScheduledDate]);

  const leagueId = sport ? SPORT_TO_LEAGUE_ID[sport] : undefined;

  const { data: fixtures = [], isLoading: fixturesLoading } = useGetFeedFixturesQuery(
    leagueId,
    undefined,
    selectedDate,
    selectedDate
  );

  const { data: teams = [] } = useGetFeedTeamsQuery(leagueId);

  const teamMap = useMemo(() => {
    const map = new Map<string, { name: string; abbreviation?: string }>();
    teams.forEach(team => {
      map.set(team.externalId, { name: team.name, abbreviation: team.abbreviation });
    });
    return map;
  }, [teams]);

  const handleFixtureChange = (fixtureId: string) => {
    const fixture = fixtures.find(f => f.externalId === fixtureId);
    if (fixture) {
      // Get team names from teamMap
      const homeTeam = teamMap.get(fixture.homeTeamId);
      const awayTeam = teamMap.get(fixture.awayTeamId);

      // Create enhanced fixture object with team names
      const enhancedFixture = {
        ...fixture,
        homeTeamName: homeTeam?.name || fixture.homeTeamId,
        awayTeamName: awayTeam?.name || fixture.awayTeamId,
        homeTeamAbbreviation: homeTeam?.abbreviation || '',
        awayTeamAbbreviation: awayTeam?.abbreviation || '',
      };

      onSelect(fixtureId, enhancedFixture);
    }
  };

  if (!leagueId) {
    return null;
  }

  return (
    <div className="space-y-4 border-t pt-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Game/Fixture Selection (Optional)</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Select a game to auto-populate teams and time. You can create a contest without a fixture
          (teams TBD) and add the game later before it starts, while the contest is still pending.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="fixture-date">
          <Calendar className="inline h-4 w-4 mr-1" />
          Date
        </Label>
        <Input
          id="fixture-date"
          type="date"
          value={selectedDate}
          onChange={e => {
            setSelectedDate(e.target.value);
            // Lock the one-shot seed so a later async Formik update can't
            // overwrite the user's deliberate choice.
            hasSeededFromInitialRef.current = true;
          }}
          disabled={disabled || !leagueId}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="fixture_id">Select Game/Fixture</Label>
        {fixturesLoading ? (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading fixtures...</span>
          </div>
        ) : fixtures.length === 0 ? (
          <Alert>
            <AlertDescription>
              No fixtures found for {selectedDate}. Try a different date.
            </AlertDescription>
          </Alert>
        ) : (
          <Select
            value={selectedFixtureId || ''}
            onValueChange={handleFixtureChange}
            disabled={disabled || !leagueId}
          >
            <SelectTrigger
              className={errors?.fixture_id && touched?.fixture_id ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Select a game/fixture" />
            </SelectTrigger>
            <SelectContent>
              {fixtures.map(fixture => {
                const homeTeam = teamMap.get(fixture.homeTeamId);
                const awayTeam = teamMap.get(fixture.awayTeamId);
                const homeName = homeTeam?.name || 'Home Team';
                const awayName = awayTeam?.name || 'Away Team';
                const date = new Date(fixture.scheduledDate);
                const dateStr = format(date, 'MMM dd, h:mm a');

                return (
                  <SelectItem key={fixture.externalId} value={fixture.externalId}>
                    {awayName} @ {homeName} - {dateStr}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        {errors?.fixture_id && touched?.fixture_id && (
          <span className="text-sm text-red-500">{errors.fixture_id as string}</span>
        )}
      </div>
    </div>
  );
}
